import { type MutableRefObject, useCallback } from 'react'

import { getComicProject, updateComicProject } from '../../api/comics'
import { isApiError, rethrowIfUnauthorized, shouldShowApiErrorToast } from '../../api/errors'
import type { ClientUiPublic } from '../../api/platformConfig'
import type { ToastShowOptions } from '../../context/ToastContext'
import { useDebouncedCallback } from '../../hooks/useRateLimitedCallback'
import { buildEpisodeSnapshots } from '../../lib/comicEditorDirty'
import { readStoredActiveEpisodeId, writeStoredActiveEpisodeId } from '../../lib/comicEditorStorage'
import {
  projectHasInFlightPageGeneration,
  reconcileProjectPageGenerations,
} from '../../lib/comicPageGenerationRecovery'
import { getActiveEpisode, withEpisodeCount } from '../../lib/comicProjectMutations'
import { mergeComicProjectAfterSave } from '../../lib/comicProjectSaveMerge'
import type { ComicEpisode, ComicProject } from '../../lib/comicTypes'

type UseComicEditorProjectPersistenceOptions = {
  clientUi: ClientUiPublic
  showToast: (options: ToastShowOptions) => string
  projectId: string | undefined
  platformConfigSynced: boolean
  comicModelsReady: boolean
  selectedEpisodeId: string | null
  selectedPanelIdRef: MutableRefObject<string | null>
  selectedPageIdRef: MutableRefObject<string | null>
  projectRef: MutableRefObject<ComicProject | null>
  loadedForProjectIdRef: MutableRefObject<string | null>
  setProject: (project: ComicProject | null) => void
  setSaving: (saving: boolean) => void
  setSavedSnapshots: (snapshots: Map<string, string>) => void
  setLoading: (loading: boolean) => void
  setEditorRefreshing: (refreshing: boolean) => void
  setLoadError: (error: string | null) => void
  setSelectedEpisodeId: (episodeId: string | null) => void
  setSelectedPageId: (pageId: string | null) => void
  setCanvasEpisodeIds: (updater: string[] | ((prev: string[]) => string[])) => void
  selectPanel: (panelId: string, episode: ComicEpisode) => void
  selectFirstInEpisode: (episode: ComicEpisode) => void
}

export function useComicEditorProjectPersistence({
  clientUi,
  showToast,
  projectId,
  platformConfigSynced,
  comicModelsReady,
  selectedEpisodeId,
  selectedPanelIdRef,
  selectedPageIdRef,
  projectRef,
  loadedForProjectIdRef,
  setProject,
  setSaving,
  setSavedSnapshots,
  setLoading,
  setEditorRefreshing,
  setLoadError,
  setSelectedEpisodeId,
  setSelectedPageId,
  setCanvasEpisodeIds,
  selectPanel,
  selectFirstInEpisode,
}: UseComicEditorProjectPersistenceOptions) {
  const persistProject = useCallback(
    async (next: ComicProject, quiet = false) => {
      setSaving(true)
      const keepPanelId = selectedPanelIdRef.current
      const keepPageId = selectedPageIdRef.current
      try {
        const saved = await updateComicProject(next, clientUi)
        const merged = mergeComicProjectAfterSave(next, saved)
        setProject(merged)
        projectRef.current = merged
        setSavedSnapshots(buildEpisodeSnapshots(merged))
        const activeEpId = selectedEpisodeId ?? merged.episodes[0]?.id ?? null
        const ep = getActiveEpisode(merged, activeEpId)
        if (keepPanelId && ep.panels.some((p) => p.id === keepPanelId)) {
          selectPanel(keepPanelId, ep)
        } else if (keepPageId && ep.pages.some((p) => p.id === keepPageId)) {
          selectedPageIdRef.current = keepPageId
          setSelectedPageId(keepPageId)
        }
        if (!quiet) {
          showToast({ message: '已保存', variant: 'success', autoCloseMs: 2200 })
        }
      } catch (e) {
        rethrowIfUnauthorized(e)
        if (shouldShowApiErrorToast(e)) {
          const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '保存失败'
          showToast({ message: msg, variant: 'error' })
        }
        throw e
      } finally {
        setSaving(false)
      }
    },
    [
      showToast,
      clientUi,
      selectPanel,
      selectedEpisodeId,
      selectedPanelIdRef,
      selectedPageIdRef,
      projectRef,
      setProject,
      setSaving,
      setSavedSnapshots,
      setSelectedPageId,
    ],
  )

  const scheduleSave = useDebouncedCallback((next: ComicProject) => {
    void persistProject(next, true)
  }, 700)

  const applyProject = useCallback(
    (next: ComicProject, options?: { save?: boolean }) => {
      const normalized = withEpisodeCount(next)
      setProject(normalized)
      projectRef.current = normalized
      if (options?.save) {
        scheduleSave.cancel()
        void persistProject(normalized, true)
      } else {
        scheduleSave(normalized)
      }
    },
    [persistProject, scheduleSave, projectRef, setProject],
  )

  const applyLoadedProject = useCallback(
    (p: ComicProject) => {
      loadedForProjectIdRef.current = projectId ?? null
      setProject(p)
      projectRef.current = p
      setSavedSnapshots(buildEpisodeSnapshots(p))
      const storedEpId = projectId ? readStoredActiveEpisodeId(projectId) : null
      const activeEpId =
        storedEpId && p.episodes.some((e) => e.id === storedEpId)
          ? storedEpId
          : p.episodes[0]?.id ?? null
      setSelectedEpisodeId(activeEpId)
      if (activeEpId && projectId) writeStoredActiveEpisodeId(projectId, activeEpId)
      if (activeEpId) setCanvasEpisodeIds(p.episodes.map((e) => e.id))
      const ep = getActiveEpisode(p, activeEpId)
      selectFirstInEpisode(ep)
    },
    [
      projectId,
      selectFirstInEpisode,
      loadedForProjectIdRef,
      projectRef,
      setProject,
      setSavedSnapshots,
      setSelectedEpisodeId,
      setCanvasEpisodeIds,
    ],
  )

  const fetchProject = useCallback(
    async (options?: { initial?: boolean; isCancelled?: () => boolean }) => {
      if (!projectId || !platformConfigSynced || !comicModelsReady) return
      const isInitial =
        options?.initial ?? (!projectRef.current || projectRef.current.id !== projectId)
      if (isInitial) {
        setLoading(true)
      } else {
        setEditorRefreshing(true)
      }
      setLoadError(null)
      try {
        let p = await getComicProject(projectId, clientUi)
        if (options?.isCancelled?.()) return
        if (projectHasInFlightPageGeneration(p)) {
          try {
            const rec = await reconcileProjectPageGenerations(p, clientUi, {
              failGeneratingWithoutTaskId: true,
              waitForInFlight: true,
            })
            if (options?.isCancelled?.()) return
            if (rec.changed) {
              p = rec.project
              try {
                const saved = await updateComicProject(p, clientUi)
                if (!options?.isCancelled?.()) {
                  p = mergeComicProjectAfterSave(p, saved)
                }
              } catch {
                /* 本地已恢复，下次保存会同步 */
              }
              if (!options?.isCancelled?.() && rec.messages.length > 0) {
                showToast({
                  message:
                    rec.messages.length === 1
                      ? rec.messages[0]!
                      : `已同步 ${rec.messages.length} 页生成结果`,
                  variant: 'success',
                  autoCloseMs: 4500,
                })
              }
            }
          } catch {
            if (!options?.isCancelled?.()) {
              showToast({
                message: '分页生成状态同步失败，已先打开项目，可稍后下拉刷新重试。',
                variant: 'warning',
                autoCloseMs: 4200,
              })
            }
          }
        }
        applyLoadedProject(p)
      } catch (e) {
        if (options?.isCancelled?.()) return
        rethrowIfUnauthorized(e)
        setLoadError(isApiError(e) ? e.message : e instanceof Error ? e.message : '加载失败')
        setProject(null)
        projectRef.current = null
      } finally {
        if (!options?.isCancelled?.()) {
          setLoading(false)
          setEditorRefreshing(false)
        }
      }
    },
    [
      projectId,
      platformConfigSynced,
      comicModelsReady,
      clientUi,
      applyLoadedProject,
      showToast,
      projectRef,
      setLoading,
      setEditorRefreshing,
      setLoadError,
      setProject,
    ],
  )

  return {
    applyProject,
    applyLoadedProject,
    fetchProject,
    mergeComicProjectAfterSave,
    persistProject,
  }
}
